import {
  BookOpenText,
  ChevronRight,
  CircleHelp,
  CreditCard,
  Home,
  Sparkles,
  X
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { aiTools, freeTools, type ToolSlug, tools } from '../data/tools';

function activeToolFromLocation(pathname: string, search: string, hash: string): ToolSlug | null {
  if (pathname === '/') {
    const hashTool = hash.startsWith('#studio-') ? hash.slice('#studio-'.length) : null;
    const legacyQueryTool = new URLSearchParams(search).get('tool');
    const match = tools.find((tool) => tool.slug === (hashTool || legacyQueryTool));
    return match?.slug ?? null;
  }

  if (pathname.startsWith('/tools/')) {
    const requested = pathname.split('/')[2];
    return tools.find((tool) => tool.slug === requested)?.slug ?? null;
  }

  return null;
}

export function StudioSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const activeTool = activeToolFromLocation(location.pathname, location.search, location.hash);

  return (
    <>
      <button
        className={`studio-sidebar-backdrop${open ? ' open' : ''}`}
        type="button"
        aria-label="Close image tools menu"
        onClick={onClose}
      />
      <aside className={`studio-sidebar${open ? ' open' : ''}`} aria-label="AI image tools">
        <div className="studio-sidebar-mobile-heading">
          <strong>Image tools</strong>
          <button type="button" onClick={onClose} aria-label="Close image tools menu"><X size={19} /></button>
        </div>

        <nav className="studio-sidebar-nav" aria-label="Studio navigation">
          <Link className={location.pathname === '/' && !location.search && !location.hash ? 'active sidebar-home-link' : 'sidebar-home-link'} to="/" onClick={onClose}>
            <Home size={18} />
            <span>Home</span>
          </Link>

          <p className="studio-sidebar-label">AI image studio</p>
          <div className="studio-sidebar-tools">
            {aiTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link key={tool.slug} className={activeTool === tool.slug ? 'active' : ''} to={`/#studio-${tool.slug}`} onClick={onClose}>
                  <Icon size={18} /><span>{tool.shortName}</span><ChevronRight className="studio-sidebar-arrow" size={15} />
                </Link>
              );
            })}
          </div>

          <p className="studio-sidebar-label">Free utilities</p>
          <div className="studio-sidebar-tools">
            {freeTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link key={tool.slug} className={activeTool === tool.slug ? 'active' : ''} to={`/#studio-${tool.slug}`} onClick={onClose}>
                  <Icon size={18} /><span>{tool.shortName}</span><ChevronRight className="studio-sidebar-arrow" size={15} />
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="studio-sidebar-bottom">
          <nav className="studio-sidebar-secondary" aria-label="Helpful links">
            <Link to="/pricing" onClick={onClose}><CreditCard size={17} /><span>Pricing</span></Link>
            <Link to="/guides" onClick={onClose}><BookOpenText size={17} /><span>Guides</span></Link>
            <Link to="/faq" onClick={onClose}><CircleHelp size={17} /><span>Help & FAQ</span></Link>
          </nav>

          <Link className="studio-sidebar-card" to="/#studio-enhance" onClick={onClose}>
            <span className="studio-sidebar-card-icon"><Sparkles size={18} /></span>
            <span>
              <strong>AI Studio</strong>
              <small>AI workflows + free utilities</small>
            </span>
            <ChevronRight size={16} />
          </Link>
        </div>
      </aside>
    </>
  );
}
